import { useState } from "react";
import { 
  CheckCircle2, 
  Phone, 
  Mail, 
  MapPin, 
  MessageCircle,
  Send,
  Shield,
  Clock,
  Award,
  Zap,
  Loader2,
  ArrowRight,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";

const QuotePage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    workType: "",
    location: "",
    surface: "",
    budget: "",
    deadline: "",
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const workTypes = [
    "Rénovation intérieure complète",
    "Plafonnage",
    "Carrelage",
    "Peinture intérieure",
    "Rénovation salle de bain",
    "Pose de sol / Parquet",
    "Autre",
  ];

  const locations = [
    "Bruxelles-Ville",
    "Uccle",
    "Ixelles",
    "Waterloo",
    "Rhode-Saint-Genèse",
    "Sint-Pieters-Leeuw",
    "Woluwe-Saint-Pierre",
    "Woluwe-Saint-Lambert",
    "Forest",
    "Anderlecht",
    "Schaerbeek",
    "Etterbeek",
    "Auderghem",
    "Autre commune"
  ];

  const budgets = [
    "Moins de 5.000€",
    "5.000€ - 10.000€",
    "10.000€ - 20.000€",
    "20.000€ - 50.000€",
    "Plus de 50.000€",
    "À définir ensemble"
  ];

  const deadlines = [
    "Urgent (< 1 mois)",
    "Court terme (1-3 mois)",
    "Moyen terme (3-6 mois)",
    "Pas de deadline précise"
  ];

  const reassuranceItems = [
    { icon: FileText, title: "Devis détaillé", description: "Chiffrage précis et transparent" },
    { icon: Zap, title: "Réponse sous 24-48h", description: "Nous vous recontactons rapidement" },
    { icon: Shield, title: "100% gratuit", description: "Sans engagement de votre part" },
    { icon: Award, title: "Visite sur place", description: "Évaluation gratuite du projet" },
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.workType) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("https://formspree.io/f/mvzwyeky", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          _subject: `🏠 Demande de devis - ${formData.workType} - ${formData.name}`,
          name: formData.name,
          phone: formData.phone,
          email: formData.email || "Non renseigné",
          workType: formData.workType,
          location: formData.location || "Non précisée",
          surface: formData.surface || "Non précisée",
          budget: formData.budget || "Non précisé",
          deadline: formData.deadline || "Non précisé",
          description: formData.description || "Pas de description",
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success("Demande de devis envoyée avec succès !");
      } else {
        throw new Error("Erreur lors de l'envoi");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer ou nous contacter par téléphone.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="quote-success" className="pt-20 min-h-screen bg-[#FAF9F7]">
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="bg-white rounded-3xl shadow-xl p-10">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="text-green-600" size={40} />
            </div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#1A1A1A] mb-4">
              Demande de devis envoyée !
            </h1>
            <p className="text-[#6B6B6B] mb-8 text-lg">
              Merci pour votre demande. Nous l'avons bien reçue et nous vous recontacterons 
              sous <strong>24 à 48 heures</strong> pour planifier une visite gratuite.
            </p>
            
            <div className="bg-[#FAF9F7] rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-[#1A1A1A] mb-3">Prochaines étapes :</h3>
              <ul className="space-y-2 text-[#6B6B6B]">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="text-green-500" size={16} />
                  Nous analysons votre demande
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="text-green-500" size={16} />
                  Nous vous appelons pour convenir d'un rendez-vous
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="text-green-500" size={16} />
                  Visite gratuite et devis détaillé
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+32488467522">
                <Button className="bg-[#B87333] hover:bg-[#9A5F2A] text-white px-6">
                  <Phone className="mr-2" size={18} />
                  Nous appeler directement
                </Button>
              </a>
              <Button
                variant="outline"
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({ name: "", phone: "", email: "", workType: "", location: "", surface: "", budget: "", deadline: "", description: "" });
                }}
                className="border-[#B87333] text-[#B87333] hover:bg-[#B87333] hover:text-white"
              >
                Nouvelle demande
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="quote-page" className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#0A0A0A] py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#B87333]/10 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-10">
            <span className="inline-block bg-[#B87333]/20 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-6">
              Devis Gratuit
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] font-bold text-white mb-6">
              Demandez votre <span className="text-copper-gradient">devis gratuit</span>
            </h1>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              Décrivez votre projet et recevez un devis détaillé sous 24-48h. 
              Visite gratuite et sans engagement.
            </p>
          </div>

          {/* Big Phone CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <a
              href="tel:+32488467522"
              className="group flex items-center gap-4 bg-gradient-to-r from-[#B87333] to-[#CD7F32] hover:from-[#CD8B4A] hover:to-[#D4AF37] text-white px-8 py-5 rounded-2xl shadow-2xl transition-all transform hover:scale-105"
            >
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                <Phone size={28} className="group-hover:animate-pulse" />
              </div>
              <div className="text-left">
                <p className="text-sm opacity-80">Ou appelez-nous</p>
                <p className="text-2xl sm:text-3xl font-bold">+32 488 46 75 22</p>
              </div>
            </a>
          </div>

          {/* Reassurance Badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {reassuranceItems.map((item, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center"
              >
                <item.icon className="text-[#B87333] mx-auto mb-2" size={28} />
                <h3 className="text-white font-semibold text-sm">{item.title}</h3>
                <p className="text-white/60 text-xs mt-1">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-[#FAF9F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-10">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-['Playfair_Display'] font-bold text-[#1A1A1A] mb-2">
                Décrivez votre projet
              </h2>
              <p className="text-[#6B6B6B]">
                Plus vous nous donnez de détails, plus notre devis sera précis.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Info */}
              <div className="bg-[#FAF9F7] rounded-xl p-6">
                <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-[#B87333] text-white rounded-full text-sm flex items-center justify-center">1</span>
                  Vos coordonnées
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Nom complet <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Téléphone <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+32 XXX XX XX XX"
                      className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]"
                      required
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Email
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="votre@email.com"
                      className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]"
                    />
                  </div>
                </div>
              </div>

              {/* Project Info */}
              <div className="bg-[#FAF9F7] rounded-xl p-6">
                <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-[#B87333] text-white rounded-full text-sm flex items-center justify-center">2</span>
                  Votre projet
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Type de travaux <span className="text-red-500">*</span>
                    </label>
                    <Select onValueChange={(value) => handleSelectChange("workType", value)}>
                      <SelectTrigger className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]">
                        <SelectValue placeholder="Sélectionnez" />
                      </SelectTrigger>
                      <SelectContent>
                        {workTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Localisation du chantier
                    </label>
                    <Select onValueChange={(value) => handleSelectChange("location", value)}>
                      <SelectTrigger className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]">
                        <SelectValue placeholder="Votre commune" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc} value={loc}>
                            {loc}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Surface approximative
                    </label>
                    <Input
                      name="surface"
                      value={formData.surface}
                      onChange={handleChange}
                      placeholder="Ex: 50 m²"
                      className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Budget envisagé
                    </label>
                    <Select onValueChange={(value) => handleSelectChange("budget", value)}>
                      <SelectTrigger className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]">
                        <SelectValue placeholder="Sélectionnez" />
                      </SelectTrigger>
                      <SelectContent>
                        {budgets.map((b) => (
                          <SelectItem key={b} value={b}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Délai souhaité
                    </label>
                    <Select onValueChange={(value) => handleSelectChange("deadline", value)}>
                      <SelectTrigger className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]">
                        <SelectValue placeholder="Quand souhaitez-vous commencer ?" />
                      </SelectTrigger>
                      <SelectContent>
                        {deadlines.map((d) => (
                          <SelectItem key={d} value={d}>
                            {d}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-[#FAF9F7] rounded-xl p-6">
                <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-[#B87333] text-white rounded-full text-sm flex items-center justify-center">3</span>
                  Description du projet
                </h3>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Décrivez votre projet en détail : état actuel, travaux souhaités, contraintes particulières..."
                  rows={5}
                  className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                data-testid="submit-quote-btn"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#B87333] to-[#CD7F32] hover:from-[#CD8B4A] hover:to-[#D4AF37] text-white py-7 rounded-xl text-lg font-semibold disabled:opacity-70"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={22} />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="mr-2" size={22} />
                    Envoyer ma demande de devis
                    <ArrowRight className="ml-2" size={22} />
                  </>
                )}
              </Button>

              <div className="flex flex-wrap justify-center gap-6 text-sm text-[#6B6B6B]">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="text-green-500" size={16} />
                  Gratuit et sans engagement
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="text-green-500" size={16} />
                  Réponse sous 24-48h
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="text-green-500" size={16} />
                  Visite gratuite
                </span>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-16 h-16 bg-[#B87333]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="text-[#B87333]" size={32} />
              </div>
              <h3 className="font-semibold text-[#1A1A1A] mb-2">Garantie décennale</h3>
              <p className="text-[#6B6B6B] text-sm">Tous nos travaux sont couverts par notre assurance professionnelle</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-[#B87333]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="text-[#B87333]" size={32} />
              </div>
              <h3 className="font-semibold text-[#1A1A1A] mb-2">Délais respectés</h3>
              <p className="text-[#6B6B6B] text-sm">Nous nous engageons sur des délais précis et nous les tenons</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-[#B87333]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="text-[#B87333]" size={32} />
              </div>
              <h3 className="font-semibold text-[#1A1A1A] mb-2">15+ ans d'expérience</h3>
              <p className="text-[#6B6B6B] text-sm">Une équipe qualifiée et professionnelle à votre service</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuotePage;
